import React, { useState, useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

const Header = ({ nodeCount, nodes }) => {
  const location = useLocation();
  const isDetailPage = location.pathname.includes('/node/');
  const nodeId = isDetailPage ? location.pathname.split('/').pop() : null;
  const [loadTime, setLoadTime] = useState('');

  useEffect(() => {
    const now = new Date();
    const hours = now.getHours();
    const minutes = now.getMinutes();
    const seconds = now.getSeconds();
    const timeString = `${hours}:${minutes.toString().padStart(2, '0')}:${seconds
      .toString()
      .padStart(2, '0')} ${hours >= 12 ? 'PM' : 'AM'}`;
    setLoadTime(timeString);
  }, []);

  useEffect(() => {
    // Initialize US map
    const usMap = L.map('usMap', {
      attributionControl: false,
      zoomControl: false, // Disable zoom controls
      dragging: false, // Disable dragging
      touchZoom: false, // Disable touch zoom
      scrollWheelZoom: false, // Disable scroll wheel zoom
      doubleClickZoom: false, // Disable double click zoom
      boxZoom: false, // Disable box zoom
      keyboard: false // Disable keyboard navigation
    }).setView([34.597042, -99.843750], 2);

    // Initialize Hawaii map
    const hawaiiMap = L.map('hawaiiMap', {
      attributionControl: false,
      zoomControl: false,
      dragging: false,
      touchZoom: false,
      scrollWheelZoom: false,
      doubleClickZoom: false,
      boxZoom: false,
      keyboard: false
    }).setView([20.385825, -156.599121], 6);

    L.tileLayer('https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}.png', {
      maxZoom: 19,
    }).addTo(usMap);

    L.tileLayer('https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}.png', {
      maxZoom: 19,
    }).addTo(hawaiiMap);

    // Plot nodes on both maps
    nodes.forEach((node) => {
      if (node.coords && node.coords.lat && node.coords.lng) {
        // Create marker with custom green circle
        const icon = L.divIcon({
          className: 'custom-div-icon',
          html: `<div style="
            width: 10px;
            height: 10px;
            background-color: #10B981;
            border-radius: 50%;
            border: 2px solid white;
            box-shadow: 0 0 3px rgba(0,0,0,0.3);
          "></div>`,
          iconSize: [10, 10],
          iconAnchor: [5, 5]
        });

        L.marker([node.coords.lat, node.coords.lng], {
          icon: icon
        }).addTo(usMap);

        L.marker([node.coords.lat, node.coords.lng], {
          icon: icon
        }).addTo(hawaiiMap);
      }
    });

    return () => {
      usMap.remove();
      hawaiiMap.remove();
    };
  }, [nodes]);

  return (
    <div className="bg-white px-6 py-8 border-b border-gray-200">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
          <div className="flex-grow">
            <h1
              className={`text-3xl font-bold mb-3 text-green-800 ${
                isDetailPage ? 'hover:text-green-700 transition-colors duration-200' : ''
              }`}
            >
              <Link to="/" className="no-underline text-green-800 hover:text-green-700 transition-colors duration-200">
                {isDetailPage ? `Node ${nodeId} - Environmental Dashboard` : 'Environmental Nodes Dashboard'}
              </Link>
            </h1>

            <div className="space-y-2">
              <p className="text-gray-700 text-lg font-medium">
                Real-time Environmental Monitoring Network
              </p>
              <p className="text-gray-600 max-w-3xl">
                A real-time monitoring interface displaying environmental sensor data from nodes across various locations.
                Get instant access to temperature, humidity, and pressure readings from our network of {nodeCount} environmental nodes.
              </p>

              <div className="flex items-center gap-4 text-sm text-gray-500 mt-2">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-green-500"></div>
                  <span>{nodeCount} Active Nodes</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                  <span>Last Updated: {loadTime}</span>
                </div>
              </div>
            </div>
          </div>

          <div className="flex-shrink-0 flex gap-4">
            {/* US Map Container */}
            <div className="space-y-2">
              <div 
                id="usMap" 
                style={{ 
                  width: '200px', 
                  height: '150px',
                  borderRadius: '8px',
                  overflow: 'hidden',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                }}
              ></div>
              <p className="text-sm text-gray-600 text-center">Continental United States</p>
            </div>
            
            {/* Hawaii Map Container */}
            <div className="space-y-2">
              <div 
                id="hawaiiMap" 
                style={{ 
                  width: '200px', 
                  height: '150px',
                  borderRadius: '8px',
                  overflow: 'hidden',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                }}
              ></div>
              <p className="text-sm text-gray-600 text-center">Hawaii</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Header;